import { PaymentMethod } from '@aaa-ncnu-ie/mypolicy-api-billing';
import { BillingDetails } from '@aaa-ncnu-ie/mypolicy-api-customers';

import { PaymentAmountType, StatusType } from '../constants/enums';

export interface ActionCreator {
  type: string;
  payload: any;
}

export interface SelectedPayment extends BillingDetails {
  amount?: string;
  paymentType: PaymentAmountType;
  policyType: string;
  displayPaymentMethod?: string;
}

export interface PaymentDetails {
  paymentDateTime: string;
  receiptNumber: string;
  externalReferenceNumber: string;
  authCode: string;
}

export type AnalyticsAccountTypes =
  | 'Checking Account'
  | 'Debit/Credit Card'
  | 'Savings Account';

export interface SelectedPaymentMethod {
  analyticsAccountType: AnalyticsAccountTypes;
  paymentToken: string;
  name: string;
  displayPaymentMethod: string;
  paymentMethod: PaymentMethod;
  zipCode?: string;
  expirationDate?: string;
}

type AutoPayBanner = {
  show: boolean;
  text: React.ReactElement | string;
  status: StatusType;
  title?: React.ReactElement | string;
};
export interface PaymentState {
  selectedBill: BillingDetails;
  selectedPayments: {
    [policyNumber: string]: SelectedPayment;
  };
  singleSelectedPayment: SelectedPayment;
  // used for storing details of the selected payment method and displaying the details on the Review/Status pages
  selectedPaymentMethod?: SelectedPaymentMethod;
  paymentDetails: {
    [policyNumber: string]: PaymentDetails;
  };
  confirmationEmail: string;
  autoPayBanner: AutoPayBanner;
}

// Initial state
export const initialState: PaymentState = {
  selectedBill: {} as BillingDetails,
  selectedPayments: {},
  singleSelectedPayment: {} as SelectedPayment,
  paymentDetails: {},
  confirmationEmail: '',
  autoPayBanner: { show: false, text: '', status: StatusType.SUCCESS },
};

// Actions
export const ADD_SELECTED_BILL = 'ADD_SELECTED_BILL';
export const ADD_SINGLE_SELECTED_PAYMENT = 'ADD_SINGLE_SELECTED_PAYMENT';
export const UPDATE_PAYMENTS = 'UPDATE_PAYMENTS';
export const SET_INITIAL_MULTI_PAYMENTS = 'SET_INITIAL_MULTI_PAYMENTS';
export const REMOVE_PAYMENT = 'REMOVE_PAYMENT';
export const CLEAR_SELECTED_PAYMENTS = 'CLEAR_SELECTED_PAYMENTS';
export const RESET_STATE = 'RESET_STATE';
export const ADD_CONFIRMATION_EMAIL = 'ADD_CONFIRMATION_EMAIL';
export const ADD_PAYMENT_DETAILS = 'ADD_PAYMENT_DETAILS';
export const ADD_PAYMENT_MESSAGE = 'ADD_PAYMENT_MESSAGE';
export const TOGGLE_PAYMENT_MESSAGE = 'TOGGLE_PAYMENT_MESSAGE';
export const SET_SELECTED_PAYMENT_METHOD = 'SET_SELECTED_PAYMENT_METHOD';
export const RESET_SELECTED_PAYMENT_METHOD = 'RESET_SELECTED_PAYMENT_METHOD';
export const SET_AUTO_PAY_BANNER = 'SET_AUTO_PAY_BANNER';

// Action creators
export const addSelectedBill = (item: BillingDetails) => {
  return { type: ADD_SELECTED_BILL, payload: item };
};
export const addSingleSelectedPayment = (item: SelectedPayment) => {
  return { type: ADD_SINGLE_SELECTED_PAYMENT, payload: item };
};

export const updateCustomerPayments = (
  payment: SelectedPayment & { policyNumber: string }
): ActionCreator => {
  return { type: UPDATE_PAYMENTS, payload: payment };
};

export const setInitialMultiPayments = (selectedPayments: {
  [policyNumber: string]: SelectedPayment;
}): ActionCreator => {
  return { type: SET_INITIAL_MULTI_PAYMENTS, payload: selectedPayments };
};

export const removePayment = (policyNumber: string): ActionCreator => {
  return { type: REMOVE_PAYMENT, payload: policyNumber };
};

export const clearSelectedPayments = () => {
  return { type: CLEAR_SELECTED_PAYMENTS };
};

export const resetPaymentState = () => {
  return { type: RESET_STATE };
};

export const resetSelectedPaymentMethod = () => {
  return { type: RESET_SELECTED_PAYMENT_METHOD };
};

export const addConfirmationEmail = (
  confirmationEmail: string
): ActionCreator => {
  return { type: ADD_CONFIRMATION_EMAIL, payload: confirmationEmail };
};

export const setSelectedPaymentMethod = (
  payload: SelectedPaymentMethod
): ActionCreator => ({ type: SET_SELECTED_PAYMENT_METHOD, payload });

export const addPaymentDetails = (response: {
  [policyNumber: string]: PaymentDetails;
}): ActionCreator => {
  return { type: ADD_PAYMENT_DETAILS, payload: response };
};

export const setAutoPayBanner = (
  autoPayBanner: AutoPayBanner
): ActionCreator => ({
  type: SET_AUTO_PAY_BANNER,
  payload: autoPayBanner,
});

// Reducer
export const reducer = (state: PaymentState, action: ActionCreator) => {
  switch (action.type) {
    case ADD_SELECTED_BILL:
      return { ...state, selectedBill: action.payload };
    case ADD_SINGLE_SELECTED_PAYMENT:
      return { ...state, singleSelectedPayment: action.payload };
    case SET_INITIAL_MULTI_PAYMENTS:
      return { ...state, selectedPayments: action.payload };
    case UPDATE_PAYMENTS: {
      const { policyNumber, ...payment } = action.payload;

      return {
        ...state,
        selectedPayments: {
          ...state.selectedPayments,
          [policyNumber]: payment,
        },
      };
    }
    case REMOVE_PAYMENT:
      return {
        ...state,
        selectedPayments: {
          ...state.selectedPayments,
          [action.payload]: {
            ...state.selectedPayments[action.payload],
            paymentType: PaymentAmountType.NO_PAYMENT,
            amount: '',
          },
        },
      };
    case ADD_CONFIRMATION_EMAIL:
      return { ...state, confirmationEmail: action.payload };
    case ADD_PAYMENT_DETAILS:
      return {
        ...state,
        paymentDetails: { ...state.paymentDetails, ...action.payload },
      };
    case RESET_STATE:
      return { ...initialState };
    case RESET_SELECTED_PAYMENT_METHOD:
      return { ...state, selectedPaymentMethod: undefined };
    case CLEAR_SELECTED_PAYMENTS:
      return { ...state, selectedPayments: {} };
    case SET_SELECTED_PAYMENT_METHOD:
      return { ...state, selectedPaymentMethod: action.payload };
    case SET_AUTO_PAY_BANNER:
      return {
        ...state,
        autoPayBanner: action.payload,
      };
    default:
      return state;
  }
};
